import { isEmpty } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { TextArea as TextAreaAtom } from 'components/atoms/input';
import { Modal } from 'components/atoms/modal';
import { useTranslate } from 'hooks/use-translation';
import { queueMacrotask } from 'utils/macrotask';

const TextArea = styled(TextAreaAtom)`
  border: 1px solid var(--border-color);
  background: var(--white);
`;

interface Props {
  initialContent: string;
  onDone: (c: string) => void;
  onCancel: () => void;
  visible?: boolean;
  title?: React.ReactNode;
}

export const SpotlightModal: React.FC<Props> = ({
  initialContent,
  onDone,
  onCancel,
  visible,
  title,
}) => {
  const translate = useTranslate();
  const [state, setState] = useState(initialContent);

  const defaultTitle = useMemo(
    () => (
      <FormattedMessage
        id={
          initialContent
            ? 'spotlight.edit_modal.title'
            : 'spotlight.create_modal.title'
        }
      />
    ),
    [initialContent],
  );

  const onOk = useCallback(() => {
    queueMacrotask(() => {
      onDone(state);
    });
  }, [onDone, state]);

  const onCancelCallback = useCallback(() => {
    onCancel();
  }, [onCancel]);

  return (
    <Modal
      visible={visible}
      title={title ?? defaultTitle}
      onOk={onOk}
      onCancel={onCancelCallback}
      okButtonProps={{
        disabled: isEmpty(state),
      }}
      okText={
        initialContent ? (
          <FormattedMessage id="done" />
        ) : (
          <FormattedMessage id="add" />
        )
      }
      destroyOnClose
    >
      <TextArea
        autoFocus
        placeholder={translate('spotlight.modal.input.placeholder')}
        rows={4}
        defaultValue={state}
        onChange={(event) => setState(event.target.value)}
        onBlur={(event) => setState(event.target.value)}
      />
    </Modal>
  );
};
